// NEW: UI_REVAMP
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import EmptyState from '../components/common/EmptyState';
import SearchFilterHeader from '../components/common/SearchFilterHeader';
import {
	MobileTransactionFilterSheet,
	TransactionFilterPanel,
} from '../components/modal/filterRecord/TransactionFilterPanels';
import { ContainerLoadingModal } from '../components/modal/GlassModal';
import AppliedTransactionFilters from '../components/transaction/AppliedTransactionFilters';
import TransactionHistory from '../components/transaction/TransactionHistory';
import useIsMobileView from '../hooks/useIsMobileView';

const getTransactionDate = (date) => (date ? new Date(date) : new Date());

const TransactionScreen = () => {
	const isMobileView = useIsMobileView();

	// outlet
	const [date, setDate, maxDate] = useOutletContext();
	// state
	const [ticketStatus, setTicketStatus] = useState('ALL');
	const [searchValue, setSearchValue] = useState('');
	const [inputValue, setInputValue] = useState('');
	const [initialTransactionDate, setInitialTransactionDate] = useState(() =>
		getTransactionDate(date),
	);
	const [filterStartDate, setFilterStartDate] = useState(() => getTransactionDate(date));
	const [filterEndDate, setFilterEndDate] = useState(() => getTransactionDate(date));
	const [hasAppliedDateRange, setHasAppliedDateRange] = useState(false);
	const [hasTransactionContent, setHasTransactionContent] = useState(true);
	const [isTransactionFetching, setIsTransactionFetching] = useState(false);
	// modal
	const { isOpen, onOpen, onClose } = useDisclosure();
	const maxSelectableDate = new Date(new Date(maxDate).setDate(new Date(maxDate).getDate() + 1));
	const isTicketNumberSearch = Boolean(inputValue.trim());

	const handleClearInput = () => {
		const today = new Date();

		setSearchValue('');
		setInputValue('');
		setInitialTransactionDate(today);
		setFilterStartDate(today);
		setFilterEndDate(today);
		setHasAppliedDateRange(false);
		setDate(today);
	};

	const handleSearch = () => {
		const nextInputValue = searchValue.trim();

		if (!nextInputValue) {
			handleClearInput();
			return;
		}

		setSearchValue(nextInputValue);
		setInputValue(nextInputValue);
	};

	const handleApplyTransactionDateRange = (start, end) => {
		const nextStartDate = start || initialTransactionDate;
		const nextEndDate = end || nextStartDate;
		const isCustomDateRange =
			!dayjs(nextStartDate).isSame(initialTransactionDate, 'day') ||
			!dayjs(nextEndDate).isSame(initialTransactionDate, 'day');

		setFilterStartDate(nextStartDate);
		setFilterEndDate(nextEndDate);
		setHasAppliedDateRange(isCustomDateRange);
		setDate(nextEndDate);
	};

	const handleClearDateRange = () => {
		setFilterStartDate(initialTransactionDate);
		setFilterEndDate(initialTransactionDate);
		setHasAppliedDateRange(false);
		setDate(initialTransactionDate);
	};

	useEffect(() => {
		if (!hasAppliedDateRange) {
			const nextDate = getTransactionDate(date);
			setInitialTransactionDate(nextDate);
			setFilterStartDate(nextDate);
			setFilterEndDate(nextDate);
		}
	}, [date, hasAppliedDateRange]);

	return (
		<Box
			display='flex'
			flexDirection='column'
			flex='1'
			position='relative'
			paddingTop={'0'}
			width='100%'
		>
			<ContainerLoadingModal
				isOpen={isTransactionFetching}
				inset={{ base: -2, lg: -4 }}
				borderRadius={{ base: 'inherit', lg: '16px' }}
			/>

			<Box display='flex' flex='1' width='100%'>
				<Flex flex='1' minHeight='0' flexDirection='column'>
					<Box position='relative' width='100%'>
						<SearchFilterHeader
							searchValue={searchValue}
							placeholder='Search Transaction ID'
							inputMode='text'
							onChangeValue={(e) => {
								setSearchValue(e.target.value);
								if (e.target.value === '') {
									handleClearInput();
								}
							}}
							onSearch={handleSearch}
							onClearSearch={handleClearInput}
							showSearchButtonWhenValue
							onFilterClick={() => {
								onOpen();
								if (!isMobileView && window.smartsupp) {
									window.smartsupp(function () {
										const container = document.getElementById('smartsupp-widget-container');
										const widget = document.getElementById('widgetButtonFrame');

										if (container?.firstElementChild) {
											container.firstElementChild.style.zIndex = '999';
										}
										if (widget?.firstElementChild) {
											widget.firstElementChild.style.zIndex = '999';
										}
									});
								}
							}}
						/>
						{!isMobileView ? (
							<TransactionFilterPanel
								isOpen={isOpen}
								onClose={onClose}
								ticketStatus={ticketStatus}
								setTicketStatus={setTicketStatus}
								selected={initialTransactionDate}
								startDate={filterStartDate}
								endDate={filterEndDate}
								onChangeDateRange={handleApplyTransactionDateRange}
								maxDate={maxSelectableDate}
							/>
						) : null}
					</Box>

					{isMobileView ? (
						<MobileTransactionFilterSheet
							isOpen={isOpen}
							onClose={onClose}
							ticketStatus={ticketStatus}
							setTicketStatus={setTicketStatus}
							selected={initialTransactionDate}
							startDate={filterStartDate}
							endDate={filterEndDate}
							onChangeDateRange={handleApplyTransactionDateRange}
							maxDate={maxSelectableDate}
						/>
					) : null}

					{!isTicketNumberSearch ? (
						<AppliedTransactionFilters
							startDate={filterStartDate}
							endDate={filterEndDate}
							transactionType={ticketStatus}
							onClearDateRange={handleClearDateRange}
							onClearTransactionType={() => setTicketStatus('ALL')}
						/>
					) : null}

					<TransactionHistory
						ticketStatus={ticketStatus}
						ticketNumber={inputValue}
						dateFrom={filterStartDate}
						dateTo={filterEndDate}
						emptyState={<EmptyState />}
						onHasDataChange={setHasTransactionContent}
						onFetchingChange={setIsTransactionFetching}
					/>
				</Flex>
			</Box>
		</Box>
	);
};

export default TransactionScreen;
